import { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import useCalculatedNPVandPayback from '@/hooks/useCalculatedNPVandPayback';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { converToMillion } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: React.Key;
  name: React.ReactNode;
  total?: string;
}

const CashFlowWithStorageTable = ({
  hurdleRate,
  defaultHurdleRate,
  simulationId,
}: {
  hurdleRate: number;
  defaultHurdleRate: number;
  simulationId?: number;
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const symbol = currencySymbol || '$';
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId}/yearly_financial_analysis/`,
      {
        revalidateOnFocus: false,
      }
    );
  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );

  const changeInCashBalanceFlows =
    financialData?.graphData.map((item) => item.changeInCashBalance) || [];

  const { calculatedNPV: adjustedNpvOfChangeInCashBalance, yearlyNPVs } =
    useCalculatedNPVandPayback(
      financialData?.graphData,
      changeInCashBalanceFlows,
      hurdleRate,
      defaultHurdleRate,
      simulationData?.result.npvOfChangeInCashBalance,
      simulationData?.result.discountedPaybackPeriodProject
    );

  if (!financialData || !simulationData) {
    return null;
  }

  const years = [
    ...new Set(financialData.graphData.map((item) => item.year)),
  ].sort((a, b) => a - b);

  const scrollX = 600 + years.length * 100;

  const getValuesForYears = (
    key: keyof SimulationResultYearlyFinancialAnalysisData
  ) => {
    const values: Record<string, string> = {};
    years.forEach((year) => {
      const data = financialData.graphData.find((item) => item.year === year);
      const yearKey = year.toString();

      if (
        key === 'npvOfChangeInCashBalance' &&
        yearlyNPVs[yearKey] !== undefined
      ) {
        values[yearKey] =
          `${converToMillion(Number(yearlyNPVs[yearKey]))} ${currencySymbol}`;
      } else {
        values[yearKey] = data
          ? `${converToMillion(data[key])} ${currencySymbol}`
          : `0 ${currencySymbol}`;
      }
    });
    return values;
  };

  const formatCurrency = (value?: number) =>
    `${converToMillion(value || 0)} ${currencySymbol}`;

  const columns: TableColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 350,
      fixed: 'left',
    },
    {
      title: t('result.finance.cashFlowTable.columns.total'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    ...years.map((year) => ({
      title: year.toString(),
      dataIndex: year.toString(),
      key: year.toString(),
      width: 100,
    })),
  ];

  const dataSource = [
    {
      key: '1',
      name: t('result.finance.cashFlowTable.rows.totalRevenue'),
      total: `${converToMillion(simulationData?.result.totalRevenue || 0)} ${currencySymbol}`,
      ...getValuesForYears('totalRevenue'),
    },
    {
      key: '3',
      name: t('result.finance.cashFlowTable.rows.costOfPurchasedEnergy', {
        symbol,
      }),
      total: `${converToMillion(simulationData?.result.costOfPurchasedEnergy || 0)} ${currencySymbol}`,
      ...getValuesForYears('costOfPurchasedEnergy'),
    },
    {
      key: '4',
      name: t(
        'result.finance.cashFlowTable.rows.preplantConsumptionBillingCost'
      ),
      total: `${converToMillion(simulationData?.result.preplantConsumptionBillingCost || 0)} ${currencySymbol}`,
      ...getValuesForYears('preplantConsumptionBillingCost'),
    },
    {
      key: '5',
      name: t('result.finance.cashFlowTable.rows.distributionCosts'),
      total: `${converToMillion(simulationData?.result.distributionCosts || 0)} ${currencySymbol}`,
      ...getValuesForYears('distributionCosts'),
    },
    {
      key: '6',
      name: t('result.finance.cashFlowTable.rows.totalOpex'),
      total: `${converToMillion(simulationData?.result.totalOpex || 0)} ${currencySymbol}`,
      ...getValuesForYears('totalOpex'),
    },
    {
      key: '7',
      name: t('result.finance.cashFlowTable.rows.incomeTax'),
      total: `${converToMillion(simulationData?.result.incomeTax || 0)} ${currencySymbol}`,
      ...getValuesForYears('incomeTax'),
    },
    {
      key: '8',
      name: t('result.finance.cashFlowTable.rows.operatingCashFlow'),
      total: formatCurrency(simulationData?.result.operatingCashFlow),
      ...getValuesForYears('operatingCashFlow'),
    },
    // Investing Cash Flow Section
    {
      key: '9',
      name: t('result.finance.cashFlowTable.rows.totalCapitalExpenditures'),
      total: formatCurrency(simulationData?.result.totalCapitalExpenditures),
      ...getValuesForYears('totalCapitalExpenditures'),
    },
    {
      key: '10',
      name: t('result.finance.cashFlowTable.rows.investingCashFlow'),
      total: formatCurrency(simulationData?.result.investingCashFlow),
      ...getValuesForYears('investingCashFlow'),
    },
    {
      key: '2',
      name: t('result.finance.cashFlowTable.rows.totalResidualValue'),
      total: `${converToMillion(simulationData?.result.totalResidualValue || 0)} ${currencySymbol}`,
      ...getValuesForYears('totalResidualValue'),
    },
    // Free Cash Flow to Firm
    {
      key: '11',
      name: t('result.finance.cashFlowTable.rows.freeCashFlowToFirm'),
      total: `${converToMillion(simulationData?.result.freeCashFlowToFirm || 0)} ${currencySymbol}`,
      ...getValuesForYears('freeCashFlowToFirm'),
    },
    // Financing Cash Flow Section
    {
      key: '12',
      name: t('result.finance.cashFlowTable.rows.totalLoanProceeds'),
      total: formatCurrency(simulationData?.result.totalLoanProceeds),
      ...getValuesForYears('totalLoanProceeds'),
    },
    {
      key: '13',
      name: t('result.finance.cashFlowTable.rows.totalLoanPrincipalPayment'),
      total: formatCurrency(simulationData?.result.totalLoanPrincipalRepayment),
      ...getValuesForYears('totalLoanPrincipalRepayment'),
    },
    {
      key: '14',
      name: t('result.finance.cashFlowTable.rows.totalLoanInterestPayment'),
      total: formatCurrency(simulationData?.result.totalLoanInterestPayment),
      ...getValuesForYears('totalLoanInterestPayment'),
    },
    {
      key: '15',
      name: t('result.finance.cashFlowTable.rows.totalEquityContribution'),
      total: formatCurrency(simulationData?.result.totalEquityContribution),
      ...getValuesForYears('totalEquityContribution'),
    },
    {
      key: '16',
      name: t('result.finance.cashFlowTable.rows.financingCashFlow'),
      total: formatCurrency(simulationData?.result.financingCashFlow),
      ...getValuesForYears('financingCashFlow'),
    },
    // Cash Balance Section
    {
      key: '17',
      name: t('result.finance.cashFlowTable.rows.changeInCashBalance'),
      total: formatCurrency(simulationData?.result.changeInCashBalance),
      ...getValuesForYears('changeInCashBalance'),
    },
    {
      key: '18',
      name: t('result.finance.cashFlowTable.rows.cashBalance'),
      total: formatCurrency(simulationData?.result.cashBalance),
      ...getValuesForYears('cashBalance'),
    },
    {
      key: '19',
      name: t('result.finance.cashFlowTable.rows.npvOfChangeInCashBalance'),
      total: formatCurrency(adjustedNpvOfChangeInCashBalance),
      ...getValuesForYears('npvOfChangeInCashBalance'),
    },
    // Free Cash Flow to Equity
    {
      key: '20',
      name: t('result.finance.cashFlowTable.rows.freeCashFlowToEquity'),
      total: formatCurrency(simulationData?.result.freeCashFlowToEquity),
      ...getValuesForYears('freeCashFlowToEquity'),
    },
    // IRR and Payback Section
    {
      key: '21',
      name: t('result.finance.cashFlowTable.rows.equityIrr'),
      total: `${(simulationData?.result.equityIrr || 0).toFixed(2)}%`,
    },
    {
      key: '22',
      name: t('result.finance.cashFlowTable.rows.projectIrr'),
      total: `${(simulationData?.result.projectIrr || 0).toFixed(2)}%`,
    },
    {
      key: '23',
      name: t('result.finance.cashFlowTable.rows.discountedPaybackPeriod'),
      total: `${simulationData?.result.discountedPaybackPeriodProject || 0} ${t('result.finance.cashFlowTable.rows.years')}`,
    },
  ];

  return (
    <StyledProfit>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: scrollX }}
      />
    </StyledProfit>
  );
};

export default CashFlowWithStorageTable;
